import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../pages/helpCenter";
import { Modal } from "./modal";
import { ICONS } from "../../../utils/Utils";

const SOURCES = ["AizoPlan", "AizoAdvisor"];

export const CategoryModal = ({ open, initial, onClose, onSave }) => {
  const { register, control, handleSubmit, watch, reset } = useForm({
    defaultValues: { name: "", description: "", icon: "", source: SOURCES[0] },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: initial?.name || "",
        description: initial?.description || "",
        icon: initial?.icon || "",
        source: initial?.source || SOURCES[0],
      });
    }
  }, [open, initial, reset]);

  const name = watch("name");
  const canSave = name.trim().length > 0;

  const onSubmit = (data) => {
    onSave({
      name: data.name.trim(),
      description: data.description.trim(),
      icon: data.icon,
      source: data.source,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Edit category" : "New category"}
      subtitle="Categories are the top level of your help center."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSave} onClick={handleSubmit(onSubmit)}>
            {initial ? "Save changes" : "Create category"}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Name <span className="text-rose-500">*</span>
          </span>
          <input
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
            autoFocus
            placeholder="e.g. Getting Started"
            {...register("name")}
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Description
          </span>
          <textarea
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 resize-none"
            rows={3}
            placeholder="What does this category cover?"
            {...register("description")}
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Icon
          </span>
          <Controller
            name="icon"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select an icon</option>

                {Object.entries(ICONS).map(([key, icon]) => (
                  <option key={key} value={key}>
                    {icon}
                  </option>
                ))}
              </select>
            )}
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Source <span className="text-rose-500">*</span>
          </span>
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <div className="flex gap-2">
                {SOURCES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => field.onChange(s)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${field.value === s
                      ? "border-cyan-600 bg-cyan-50 text-cyan-700"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          />
        </label>
      </form>
    </Modal>
  );
};